(ns storrito.connect3.recovery-panel
  "The panels of the code login (handleRecovery.php): after Instagram
   rejected the password, it sends a login code to one of the account's
   contact points and the code establishes the session instead."
  (:require [clojure.string :as str]
            [storrito.connect3.common :as common]
            [storrito.ui.button :as button]))

(def method-labels
  ;; handleRecovery.php lists 'password' only next to code methods and
  ;; only when a password was entered: picking it logs in with that
  ;; password instead of a code.
  {"email" "Email"
   "phone" "Text message"
   "whatsapp" "WhatsApp"
   "password" "Use my password instead"})

(defn password-option?
  "True when the picked option is the password, not a code."
  [state selected]
  (boolean (some (fn [{:keys [id auth-option]}]
                   (and (= (str id) selected)
                        (= auth-option "password")))
                 (:options state))))

(defn method-label
  [method]
  (get method-labels method method))

(defn option-label
  "The label of a `select-recovery-method` option: the method, with the
   masked contact point Instagram would send the code to."
  [{:keys [auth-option contact-point]}]
  (if (seq contact-point)
    (str (method-label auth-option) ": " contact-point)
    (method-label auth-option)))

(defn valid-code?
  "The length of Instagram's login codes depends on the contact point: 6
   digits by SMS, 8 for backup-style codes and 10 in the account-recovery
   emails. Some SMS codes are letters, not digits (seen 2026-09-29 and
   2026-10-10: nine letters, brain-102). Instagram checks the code itself
   (the library sends it as a string), so only require enough letters or
   digits to be a code at all."
  [code]
  (boolean (re-matches #"[A-Za-z0-9]{6,}" (or code ""))))

(defn- code-input-validation
  [event]
  (set! event.target.value
        (str/replace event.target.value #"[^A-Za-z0-9]" "")))

(defn code-panel
  "Statuses `recovery-code-required` and `recovery-code-wrong`."
  [state]
  (let [code (:recovery-code state)
        valid? (valid-code? code)
        contact-point (:contact-point state)
        submit [[:connect3/send! {:recovery-code code}]]]
    [:div.flex.items-center.justify-center
     [:div.bg-white.shadow-xl.rounded-2xl.px-8.pt-6.pb-6.max-w-md.w-full.mx-auto.flex.flex-col.items-center
      (common/instagram-logo)

      [:div.flex.items-center.justify-center.bg-blue-100.text-blue-600.rounded-full.mt-4.mb-3
       {:style {:width "56px" :height "56px"}}
       [:i.fas.fa-key.fa-lg]]

      [:h2.text-2xl.font-bold.text-gray-900.mb-1.text-center.w-full
       "Enter the login code"]
      [:p.text-base.text-gray-700.mb-4.text-center.w-full
       "Instagram sent a login code to "
       (if (seq contact-point)
         [:b contact-point]
         (str "your " (str/lower-case (method-label (:method state)))))
       "."]

      [:div.w-full.bg-blue-50.border.border-blue-200.rounded-lg.p-3.mb-4
       [:p.text-sm.font-semibold.text-gray-800.mb-2.flex.items-center
        [:i.fas.fa-info-circle.text-blue-600.mr-2]
        "Can't find the code?"]
       [:ul.text-sm.text-gray-700.list-disc.pl-5.space-y-1
        [:li "Give it up to a minute — Instagram messages can be slow."]
        [:li "Check your spam or promotions folder."]
        [:li "Use the newest code if you received several."]]]

      [:div.flex.flex-col.justify-center.items-center.w-full
       ;; Wrapper div for password-manager compatibility, see comment in
       ;; `storrito.connect3.login-panel/panel`.
       [:div
        [:input#recovery-code.px-5.py-3.placeholder-gray-500.border-gray-400.rounded-md.border-2.text-center.text-2xl.tracking-widest.bg-gray-50.focus:outline-none.focus:ring-2.focus:ring-blue-500.focus:border-blue-500.transition-all.duration-200.mb-2
         {:style {:width "200px"}
          :name "recovery-code"
          :type "text"
          :required true
          :placeholder "- - - - - -"
          :autoFocus true
          ;; the same handler split as the 2FA code panels: the value is
          ;; stored on change, the input event only keeps letters and digits
          :on {:change [[:store/assoc-in [:connect3 :recovery-code] :event/target.value]]
               :input code-input-validation
               :keyup (when valid?
                        [[:js/on-enter]
                         [:connect3/send! {:recovery-code code}]])}}]]
       (when (and (seq code) (not valid?))
         [:p.text-xs.text-red-500.mb-2 "Please enter the code from Instagram (at least 6 letters or digits)."])
       (common/error-message state)
       (button/button-primary-lg
         {:class "w-full mt-3"
          :on {:click (when valid? submit)}}
         "Log in")
       (when (:try-another-way state)
         [:a.text-xs.text-blue-600.hover:underline.cursor-pointer.mt-4
          {:on {:click [[:connect3/send! {:recovery-another-way true}]]}}
          "Didn't get the code? Try another way"])]]]))

(defn valid-captcha?
  "Facebook's text captchas are a handful of letters and digits."
  [text]
  (boolean (re-matches #"[A-Za-z0-9]{3,12}" (str/trim (or text "")))))

(defn captcha-panel
  "Status `recovery-captcha-required`: Instagram guards the SMS and
   WhatsApp code delivery with a text captcha (handleRecovery.php shows
   it here instead of guessing). `:image` is a data URL fetched through
   the session's proxy, `:image-url` the fallback when that fetch
   failed."
  [state]
  (let [text (:recovery-captcha state)
        valid? (valid-captcha? text)
        submit [[:connect3/send! {:recovery-captcha (str/trim (or text ""))}]]]
    [:div.flex.items-center.justify-center
     [:div.bg-white.shadow-xl.rounded-2xl.px-8.pt-6.pb-6.max-w-md.w-full.mx-auto.flex.flex-col.items-center
      (common/instagram-logo)

      [:div.flex.items-center.justify-center.bg-blue-100.text-blue-600.rounded-full.mt-4.mb-3
       {:style {:width "56px" :height "56px"}}
       [:i.fas.fa-shield-alt.fa-lg]]

      [:h2.text-2xl.font-bold.text-gray-900.mb-1.text-center.w-full
       "One quick check before Instagram sends the code"]
      [:p.text-base.text-gray-700.mb-4.text-center.w-full
       "Enter the letters and numbers you see in the image."]

      [:img.rounded-md.border.border-gray-300.bg-gray-50.mb-3
       {:src (or (:image state) (:image-url state))
        :alt "Captcha"
        :style {:max-width "100%"}}]
      (when-let [audio (:audio-url state)]
        [:a.text-xs.text-blue-600.hover:underline.mb-3
         {:href audio :target "_blank" :rel "noopener"}
         [:i.fas.fa-volume-up.mr-1]
         "Can't read it? Listen to an audio version"])

      [:div.flex.flex-col.justify-center.items-center.w-full
       [:div
        [:input#recovery-captcha.px-5.py-3.placeholder-gray-500.border-gray-400.rounded-md.border-2.text-center.text-2xl.tracking-widest.bg-gray-50.focus:outline-none.focus:ring-2.focus:ring-blue-500.focus:border-blue-500.transition-all.duration-200.mb-2
         {:style {:width "220px"}
          :name "recovery-captcha"
          :type "text"
          :required true
          :autoComplete "off"
          :placeholder "letters & numbers"
          :autoFocus true
          :value (or text "")
          :on {:input [[:store/assoc-in [:connect3 :recovery-captcha] :event/target.value]]
               :keyup (when valid?
                        [[:js/on-enter]
                         (first submit)])}}]]
       (common/error-message state)
       (button/button-primary-lg
         {:class "w-full mt-3"
          :on {:click (when valid? submit)}}
         "Continue")
       (when (:try-another-way state)
         [:a.text-xs.text-blue-600.hover:underline.cursor-pointer.mt-4
          {:on {:click [[:connect3/send! {:recovery-another-way true}]]}}
          "Skip the SMS code and use email instead"])]]]))

(defn selected-option
  "The id of the option the user picked, falling back to the first one.
   Radio values arrive as strings, ids as numbers."
  [state]
  (or (some-> (:recovery-option state) str)
      (some-> (:id (first (:options state))) str)))

(defn method-panel
  "Status `select-recovery-method`."
  [state]
  (let [selected (selected-option state)]
    [:div.flex.items-center.justify-center
     [:div.bg-white.shadow-xl.rounded-2xl.px-8.pt-6.pb-6.max-w-md.w-full.mx-auto.flex.flex-col.items-center
      (common/instagram-logo)

      [:div.text-center.mb-5.mt-4
       [:h2.font-bold.text-2xl.text-center
        "Where should Instagram send the login code?"]]

      [:div.flex.flex-col.items-start.gap-3
       (for [{:keys [id] :as option} (:options state)]
         [:label.inline-flex.items-center
          {:replicant/key id
           :style {:cursor "pointer"}}
          [:input.h-5.w-5.text-gray-600
           {:type "radio"
            :name "recovery-option"
            :checked (= selected (str id))
            :value (str id)
            :on {:input [[:store/assoc-in [:connect3 :recovery-option] :event/target.value]]}}]
          [:span.ml-2.text-gray-700 (option-label option)]])]

      (common/error-message state)

      (button/button-primary-lg
        {:class "w-full mt-5"
         :disabled (nil? selected)
         ;; handleRecovery.php reads the reply under "recovery-option":
         :on {:click (when selected
                       [[:connect3/send! {:recovery-option selected}]])}}
        (if (password-option? state selected)
          "Log in with password"
          "Send code"))]]))
